import { useState } from 'react';
import clsx from 'clsx';
import { aud } from '@/utils/format';
import { dayCoverTimeline, type PaymentSteer } from '@/domain/cashFlow';
import { isDoubleDay } from '@/domain/workload';
import { useBrainStore } from '@/state/brainStore';
import { finishTime } from '@/screens/payouts/payoutLabels';
import type { StudioId } from '@/domain/types';

interface Props {
  studioId: StudioId;
  dayIso: string;
  /** The steer driving the banner (projected at checkout): verdict + on-hand + gap. */
  steer: PaymentSteer;
  /** Un-projected steer (checkout only): "cash in till now", before this sale. */
  baseSteer?: PaymentSteer;
  /** Include the per-artist cover table (checkout). Omit on the payout screen, which already shows cover cards. */
  showCoverTable?: boolean;
}

function Row({
  label,
  value,
  strong,
  accent,
}: {
  label: string;
  value: string;
  strong?: boolean;
  accent?: 'mint' | 'blood';
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt className={strong ? 'text-cream' : 'text-cream-soft'}>{label}</dt>
      <dd
        className={clsx(
          'font-mono tabular-nums',
          accent === 'mint' && 'text-mint',
          accent === 'blood' && 'text-blood font-semibold',
          !accent && (strong ? 'text-cream font-semibold' : 'text-cream'),
        )}
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * Read-only "Why cash?" explainer for the cash-vs-card steer (Prompt H). Collapsed
 * by default; expands to show the rule as a formula with live values, the running
 * cash math, an optional per-artist cover table, and the 6pm / double-day driver.
 * Presentation only - reads what cashFlow already computes, never recomputes.
 * Shared by the checkout PaymentPanel and the payout screen.
 */
export default function SteeringExplainer({ studioId, dayIso, steer, baseSteer, showCoverTable = false }: Props) {
  const [open, setOpen] = useState(false);
  const bookings = useBrainStore((s) => s.bookings);
  const cash = steer.method === 'cash';

  const cover = showCoverTable ? dayCoverTimeline(studioId, dayIso).filter((c) => !c.signed) : [];
  const dayKey = dayIso.slice(0, 10);
  const isDoubleArtist = (artistId: string): boolean =>
    isDoubleDay(Object.values(bookings).filter((b) => b.artistId === artistId && b.start.slice(0, 10) === dayKey));
  const doubleNames = [...new Set(cover.filter((c) => isDoubleArtist(c.artistId)).map((c) => c.artistName))];

  const thisSale = baseSteer ? steer.workingCashAud - baseSteer.workingCashAud : 0;
  const op = cash ? '>' : '<=';

  return (
    <div className="mt-2" data-testid="steering-explainer">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="steering-explainer-toggle"
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-full border border-gold-bright/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gold-bright transition hover:bg-gold-bright/10"
      >
        {cash ? 'Why cash?' : 'Why?'} {open ? '▾' : '▸'}
      </button>

      {open ? (
        <div
          className="mt-2 rounded-lg border border-gold-dim/40 bg-ink-dark/40 p-3 space-y-3"
          data-testid="steering-explainer-body"
        >
          {/* The rule */}
          <div>
            <p className="label-small-caps text-cream-faint mb-1">The rule</p>
            <p className="font-mono text-[12px] text-cream">
              cash needed by 6pm {op} cash on hand →{' '}
              <span className={clsx('font-semibold', cash ? 'text-blood' : 'text-mint')}>
                {cash ? 'ask for cash' : 'card is fine'}
              </span>
            </p>
            <p className="font-mono text-[12px] text-cream-soft">
              {aud(steer.payoutsStillDueAud)} {op} {aud(steer.workingCashAud)}
            </p>
            <p className="text-[11px] text-cream-faint mt-1">
              Deadline 6pm · buffer $0 · float excluded. The exact rule is still an open client question.
            </p>
          </div>

          {/* The math */}
          <div>
            <p className="label-small-caps text-cream-faint mb-1">The math, right now</p>
            <dl className="text-[13px] space-y-0.5">
              <Row label="Need by 6pm" value={aud(steer.payoutsStillDueAud)} />
              {baseSteer ? <Row label="Cash in till now" value={aud(baseSteer.workingCashAud)} /> : null}
              {baseSteer && thisSale > 0 ? (
                <Row label="This sale, if cash" value={`+ ${aud(thisSale)}`} accent="mint" />
              ) : null}
              <Row
                label={baseSteer ? 'Would have by 6pm' : 'Cash on hand'}
                value={aud(steer.workingCashAud)}
                strong
              />
              {steer.shortfallAud > 0 ? (
                <Row label="Still short" value={aud(steer.shortfallAud)} accent="blood" />
              ) : (
                <Row label="Covered" value="yes" accent="mint" />
              )}
            </dl>
          </div>

          {/* Cover table (checkout) */}
          {showCoverTable && cover.length > 0 ? (
            <div>
              <p className="label-small-caps text-cream-faint mb-1">Who the cash is for</p>
              <table className="w-full text-[12px]" data-testid="steering-cover-table">
                <thead>
                  <tr className="text-cream-faint text-left">
                    <th className="font-normal pb-1">Artist</th>
                    <th className="font-normal pb-1">Paid by</th>
                    <th className="font-normal pb-1 text-right">Envelope</th>
                    <th className="font-normal pb-1 text-right">Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {cover.map((c) => (
                    <tr key={c.artistId} className="border-t border-gold-dim/20">
                      <td className="py-1 text-cream">
                        {c.artistName}
                        {isDoubleArtist(c.artistId) ? (
                          <span className="ml-1 text-[9px] uppercase tracking-wide text-gold-bright">· double</span>
                        ) : null}
                      </td>
                      <td className="py-1 font-mono text-cream-soft">{finishTime(c.shiftEndIso)}</td>
                      <td className="py-1 font-mono text-right text-cream-soft">{aud(c.outstandingAud)}</td>
                      <td className={clsx('py-1 text-right font-semibold', c.covered ? 'text-mint' : 'text-blood')}>
                        {c.covered ? 'yes' : `- ${aud(c.shortfallAud)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {/* The driver */}
          {cash ? (
            <div className="rounded-md border border-gold-bright/40 bg-gold/10 p-2 text-[12px] text-cream" data-testid="steering-driver">
              The deadline is the <span className="text-gold-bright font-semibold">6pm clock-off</span>.
              {doubleNames.length > 0
                ? ` ${doubleNames.join(', ')} ${doubleNames.length === 1 ? 'works' : 'work'} a double day, so the full payout is due at 6pm, before the evening block.`
                : ' Everybody is paid by 6pm, so the till has to hold the envelopes by then.'}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
